export interface FileValidationResult {
  errorMessage: string | null;
  valid: boolean;
}

const MAX_FILE_SIZE_MB = 5;

export function validateFile(
  file: File,
  maxFileSize: number,
  acceptedTypes: string[]
): FileValidationResult {
  if (file.size > maxFileSize) {
    return {
      errorMessage: `Selecione até 5 arquivos de no máximo ${MAX_FILE_SIZE_MB}MB cada`,
      valid: false,
    };
  }

  if (!acceptedTypes.includes(file.type)) {
    return {
      errorMessage: `Apenas arquivos PNG, JPEG e PDF são compatíveis.`,
      valid: false,
    };
  }

  return {
    errorMessage: null,
    valid: true,
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export function generateUniqueFileName(fileName: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const ext = fileName.substring(fileName.lastIndexOf("."));
  return `${timestamp}_${random}${ext}`;
}

export function fileListToArray(fileList: FileList): File[] {
  return Array.from(fileList);
}

export function validateFiles(
  files: FileList | null,
  maxFiles: number,
  maxFileSize: number,
  acceptedTypes: string[]
): { errorMessage: string | null } {
  if (!files || files.length === 0) {
    return { errorMessage: null };
  }

  if (files.length > maxFiles) {
    return {
      errorMessage: `Selecione até ${maxFiles} imagens de no máximo ${MAX_FILE_SIZE_MB}MB cada`,
    };
  }

  const fileArray = Array.from(files);

  for (const file of fileArray) {
    const validationResult = validateFile(file, maxFileSize, acceptedTypes);
    if (!validationResult.valid) {
      return { errorMessage: validationResult.errorMessage };
    }
  }

  return { errorMessage: null };
}

export const formatFileName = (filename: string): string => {
  const parts = filename.split(".");
  const name = parts.slice(0, -1).join(".");
  const extension = parts.pop();

  if (name.length > 4) {
    return name.substring(0, 4) + "..." + `.${extension}`;
  }

  return filename;
};

export function validateNewFileSelection(
  newFiles: File[],
  existingFiles: File[],
  maxFiles: number,
  maxFileSize: number,
  acceptedTypes: string[]
): { errorMessage: string | null; validFiles: File[] } {
  const totalAfterAdd = existingFiles.length + newFiles.length;
  if (totalAfterAdd > maxFiles) {
    return {
      errorMessage: `Selecione até ${maxFiles} arquivos de no máximo ${MAX_FILE_SIZE_MB}MB cada.`,
      validFiles: [],
    };
  }

  const nameDuplicates = newFiles.filter((newFile) =>
    existingFiles.some((existingFile) => existingFile.name === newFile.name)
  );

  if (nameDuplicates.length > 0) {
    return {
      errorMessage: `O arquivo "${nameDuplicates[0].name}" já foi adicionado.`,
      validFiles: [],
    };
  }

  for (const file of newFiles) {
    const result = validateFile(file, maxFileSize, acceptedTypes);
    if (!result.valid) {
      return {
        errorMessage: result.errorMessage,
        validFiles: [],
      };
    }
  }

  return {
    errorMessage: null,
    validFiles: newFiles,
  };
}
